{
  "schools": {
    "landing": {
      "seo": {
        "title": "Anyway for Schools | Career-connected classroom activities",
        "description": "Bridge the gap between curriculum and careers with ready-to-run activities, interactive School Sessions and automated classroom impact reporting."
      },
      "nav": {
        "teachers": "Teachers",
        "leaders": "Leaders",
        "advisors": "Advisors",
        "rewards": "Rewards",
        "createAccount": "Create free account",
        "haveAccount": "Already have an account?",
        "signInMenu": {
          "teacher": {
            "label": "I’m a teacher",
            "description": "Sign in to your school account"
          },
          "student": {
            "label": "I’m a student",
            "description": "Continue your activities"
          }
        }
      },
      "hero": {
        "title": "Give students the why behind what they are learning.",
        "description": "Use ready-to-run activities to connect your subject to real careers and future pathways in minutes, with almost no extra prep.",
        "ctaPrimary": "Try a free activity",
        "ctaSecondary": "See how it works",
        "earlyAccess": "Early access"
      },
      "problem": {
        "title": "Students ask, {highlight}",
        "titleHighlight": "“When will I ever use this?”",
        "description": "When learning feels disconnected from the future, motivation drops. Building that bridge takes time and career context most teachers do not have. Anyway gives you the bridge ready-made."
      },
      "activities": {
        "title": "Choose, don’t create.",
        "available": "Available",
        "cards": {
          "find": {
            "title": "Find one that fits your lesson.",
            "description": "Choose a short, ready-to-run activity connected to your subject, topic or skill using our lesson builder."
          },
          "personalise": {
            "title": "Make it personal for every student.",
            "description": "Students explore careers and pathways through their own interests, with guided reflection from the AI coach."
          },
          "report": {
            "title": "Show what changed.",
            "description": "An automatic impact report captures student engagement, relevance and connection to future pathways."
          }
        }
      },
      "howItWorks": {
        "label": "How it works",
        "title": "From activity to insight in one class period.",
        "items": {
          "choose": "Choose an activity that fits what you are teaching.",
          "share": "Share it with a link or QR code. Students explore and reflect.",
          "coach": "The AI coach guides each student through the activity.",
          "feedback": "Switch on feedback to see an automatic impact report."
        }
      },
      "impactReport": {
        "title": "Prove the lesson mattered {highlight}",
        "titleHighlight": "without extra reporting.",
        "description": "See how students responded, what felt relevant and where their curiosity moved next. Share the report with school leaders or use it to shape the next conversation."
      },
      "teacherControl": {
        "title": "{highlight} stay the teacher.",
        "titleHighlight": "You",
        "description": "The AI coach guides exploration and reflection inside the activity. You choose when to use it, connect it to your subject and lead the classroom conversation."
      },
      "faqs": {
        "title": "Common questions"
      },
      "payoff": {
        "title": "Give your students a reason to care about the next lesson.",
        "description": "Create a free teacher account and try one ready-to-run activity.",
        "cta": "Try a free activity"
      }
    },
    "advisorsLanding": {
      "hero": {
        "title": "Lead career readiness across the whole school, without doing it all yourself.",
        "description": "Anyway turns student exploration and classroom activity into a living Career Development Plan - extending advisor reach and enabling more meaningful guidance at scale.",
        "earlyAccess": "Early access",
        "illustrationTagLabel": "Advisors",
        "ctaPrimary": "Request a demo"
      },
      "support": {
        "title": "Give every student more support, {highlight}",
        "titleHighlight": "without adding more to your workload.",
        "description": "Large caseloads make it hard to stay connected with every student consistently. Anyway extends career exploration into the classroom, giving advisors more context and helping meaningful guidance reach more students."
      },
      "activities": {
        "title": "Help every student build a plan for what comes next.",
        "available": "Available",
        "cards": {
          "reach": {
            "title": "Reach more students",
            "description": "Teachers bring career-connected activities into everyday learning, helping every student begin exploring and contributing to their plan."
          },
          "build": {
            "title": "Build plans from real student activity.",
            "description": "Exploration, interests, skills, goals, reflections and portfolio evidence come together in a Career Development Plan that grows with the student."
          },
          "converse": {
            "title": "Start better conversations.",
            "description": "Use each student's plan and emerging signals to decide where follow-up could help and what to discuss."
          }
        }
      },
      "livingPlan": {
        "title": "Turn everyday exploration into a living {highlight}",
        "titleHighlight": "Career Development Plan.",
        "items": {
          "launch": "Teachers launch ready-to-run career-connected activities.",
          "explore": "Students explore their interests, skills and future pathways with the AI coach.",
          "contribute": "Their reflections, goals and experiences contribute to their Career Development Plan",
          "review": "Advisors review the plan, add context and decide where a conversation could help."
        }
      },
      "judgment": {
        "title": "Built to {highlight} not replace it.",
        "titleHighlight": "support your judgment,",
        "description": "Anyway helps students capture their interests, goals and experiences, but it does not decide what pathway they should follow. Advisors use professional judgment to help students interpret their plan, consider their options and decide what comes next."
      },
      "impactReport": {
        "label": "Sample impact report",
        "title": "A Career Development Plan built from real student activity.",
        "description": "See each student's interests, skills, goals, activity history, reflections and portfolio evidence in one place. Use the plan to prepare for conversations, track development over time and help students identify their next steps.",
        "stats": {
          "engagement": {
            "title": "Subject Engagement",
            "caption": "More motivated to engage with this subject"
          },
          "jobConnections": {
            "title": "Job Connections",
            "caption": "Better understood how this subject connects to real jobs"
          },
          "discovery": {
            "title": "Career Discovery",
            "caption": "Discovered or considered a new career pathway"
          }
        }
      },
      "benefitCards": {
        "connect": {
          "title": "Connect classroom learning to career planning",
          "description": "Anyway supports your existing career development planning process by extending exploration into classrooms and bringing the resulting student evidence into one living plan."
        },
        "insights": {
          "title": "Bring student insights into career planning",
          "description": "It gives advisors more context without asking them to run every activity themselves."
        }
      },
      "payoff": {
        "title": "See how student activity becomes a meaningful Career Development Plan.",
        "description": "See how students build their plans and how advisors use them to guide conversations and next steps.",
        "cta": "Request a demo"
      }
    },
    "rewardsLanding": {
      "hero": {
        "title": "Give students another reason to take part.",
        "description": "Where rewards are enabled, participation in Anyway activities contributes to shared classroom goals and school-approved rewards - adding motivation while keeping the focus on learning and exploration.",
        "tagLabel": "Optional classroom rewards",
        "illustrationTagLabel": "Rewards",
        "ctaPrimary": "Request a demo"
      },
      "activities": {
        "title": "Rewards that recognize effort, not outcomes",
        "available": "Available",
        "cards": {
          "participation": {
            "title": "Reward participation, not particular answers.",
            "description": "Students are recognized for engaging, exploring and reflecting - not for choosing a specific career or pathway."
          },
          "momentum": {
            "title": "Build shared classroom momentum.",
            "description": "Students contribute toward a common class goal, creating another reason to participate together."
          },
          "control": {
            "title": "Schools stay in control.",
            "description": "Schools decide whether rewards are enabled and which reward options are appropriate for their students."
          }
        }
      }
    },
    "leadersLanding": {
      "hero": {
        "title": "Make career readiness a whole-school system.",
        "description": "Bring career-connected learning into everyday teaching and turn student engagement into evidence for planning, reporting and funding conversations.",
        "illustrationTagLabel": "Leaders",
        "ctaPrimary": "See the whole-school model",
        "comingSoon": "Coming soon"
      },
      "problem": {
        "title": "Career readiness cannot {highlight}",
        "titleHighlight": "depend on one office.",
        "description": "Counselors cannot reach every student alone. The result is fragmented career education and too little usable data for leaders to prove - or improve - career readiness."
      },
      "activities": {
        "title": "One connected model across teaching, guidance and leadership.",
        "available": "Available",
        "cards": {
          "teachers": {
            "title": "Teachers create the moments.",
            "description": "Ready-to-run activities bring career relevance into lessons teachers are already teaching."
          },
          "advisors": {
            "title": "Advisors get earlier context.",
            "description": "Student interests, questions, confidence and Career Development Plan progress give advisors a stronger starting point for follow-up."
          },
          "leaders": {
            "title": "Leaders get evidence they can use.",
            "description": "See participation, engagement, relevance and future-readiness signals that can support planning, reporting and funding conversations."
          }
        }
      },
      "howItWorks": {
        "title": "From classroom activity to {highlight}",
        "titleHighlight": "school-wide evidence.",
        "items": {
          "launch": "Teachers launch ready-to-run career-connected activities.",
          "explore": "Students explore, reflect and plan with our AI Coach, Blinky.",
          "guide": "Information feeds into guidance and leadership conversations."
        }
      },
      "schoolReview": {
        "title": "Built for school review",
        "items": {
          "security": {
            "title": "Privacy & security",
            "description": "Review student privacy, data use, AI safeguards, security and implementation information before rollout."
          },
          "responsibleAI": {
            "title": "Responsible AI & transparency",
            "description": "Anyway is designed to align with FERPA requirements."
          },
          "studentWellbeing": {
            "title": "Student Wellbeing",
            "description": "Student personally identifiable information is not used to train AI models, and access to student information is limited according to roles and permissions."
          }
        }
      },
      "repeatableAdoption": {
        "title": "Start with teacher value.",
        "titleHighlight": "Build repeatable adoption.",
        "description": "Teachers use activities because they fit existing instruction and help students understand why their learning matters. Repeat use creates consistent student participation and a practical path from classroom adoption to school-wide implementation."
      },
      "studentEngagement": {
        "title": "Turn student engagement into evidence you can act on.",
        "label": "How it works",
        "description": "See how students are participating and responding across career-connected activities. Bring together signals on engagement, relevance, confidence, career interests and Career Development Plan progress to support planning, reporting, improvement and funding conversations  tied to Title I, Perkins V, and ESSA Title IV-A."
      },
      "payoff": {
        "title": "See how the whole-school model works.",
        "description": "See how classroom activity drives student engagement, supports career development planning and creates useful evidence for advisors and leaders.",
        "cta": "Request a whole-school demo"
      }
    },
    "requestDemo": {
      "seo": {
        "title": "Request a demo | Anyway for Schools",
        "description": "See Anyway for Schools in action. Request a demo and we’ll walk your school through career-connected classroom activities, School Sessions and impact reporting."
      }
    },
    "login": {
      "noAccountPrompt": "Don’t have an account yet?",
      "signUpLink": "Sign up"
    },
    "signup": {
      "haveAccountPrompt": "Already have an account?",
      "signInLink": "Sign in",
      "badge": "Teacher sign up",
      "title": "Guide every student to what’s next.",
      "subtitle": "Set up your teacher account in a couple of minutes and bring Anyway into your classroom.",
      "formTitle": "Create your account",
      "benefits": {
        "heading": {
          "title": "See where your students are heading",
          "description": "Their interests, goals and next steps in one view."
        },
        "explore": {
          "title": "Help students explore their own interests",
          "description": "Students work through lessons at their own pace, guided by Blinky, our AI-powered coach."
        },
        "takeaway": {
          "title": "See what they take away",
          "description": "Class-level reports capture whether student motivation, engagement, and connection to learning increased."
        }
      },
      "form": {
        "firstNameLabel": "First name",
        "firstNamePlaceholder": "Jordan",
        "lastNameLabel": "Last name",
        "lastNamePlaceholder": "Lee",
        "emailLabel": "Your email",
        "emailPlaceholder": "name{'@'}school.edu",
        "schoolLabel": "Your school",
        "schoolPlaceholder": "Enter your school",
        "roleLabel": "Your role",
        "terms": "I confirm I am 18 or over and agree to the {termsLink} and {privacyLink}",
        "marketingOptIn": "Email me Anyway teaching resources, product updates, offers and chances to give feedback",
        "termsLinkText": "Terms of Use",
        "privacyLinkText": "Privacy policy",
        "termsRequired": "You need to confirm you are 18 or over and agree to the Terms of Use and Privacy policy",
        "submit": "Submit"
      }
    },
    "account": {
      "title": "My Account",
      "subtitle": "Manage how you sign in and how you appear to students.",
      "sections": {
        "details": {
          "title": "Your details",
          "description": "How your name and role appear to students and other teachers."
        },
        "emails": {
          "title": "Sign-in emails",
          "description": "The email addresses you can use to log in. Verify a backup so you never get locked out."
        },
        "password": {
          "title": "Password",
          "description": "Set a password so you can log in without waiting for an emailed code. Changing it needs a fresh code."
        },
        "danger": {
          "title": "Close your account",
          "description": "Permanently remove your profile and data. Restorable within 30 days of deletion."
        }
      }
    },
    "lessonBuilder": {
      "title": "Build a Lesson",
      "subtitle": "Start building",
      "description": "Tell us the topic and who it’s for. We’ll pull the best matches from the catalog below into a shareable lesson in about a minute.",
      "backToHub": "Back to Lesson Hub",
      "new": "New",
      "formHeading": "Tell us about your lesson",
      "briefLabel": "Lesson brief",
      "briefPlaceholder": "e.g. An introduction to careers in renewable energy for 10th grade students.",
      "audienceLabel": "Target audience",
      "audiencePlaceholder": "e.g. 10th grade STEM students",
      "curriculum": {
        "label": "Curriculum (optional)",
        "description": "Narrow the lesson to parts of a curriculum. Pick a framework, then go as deep as you need — choosing a broader level covers everything beneath it.",
        "add": "Add curriculum",
        "remove": "Remove {node}",
        "unknownFramework": "Unknown framework",
        "empty": "No curriculum chosen — the lesson will draw from the whole library.",
        "addAnother": "Add another",
        "dialogHeading": "Choose curriculum",
        "dialogDescription": "Pick a framework, then go as deep as you need. A broader level covers everything tagged beneath it.",
        "addNode": "Add",
        "alreadyAdded": "Already added",
        "chosen": "Chosen",
        "chosenEmpty": "Nothing chosen yet.",
        "frameworksLoadError": "We couldn’t load your school’s curriculum.",
        "retry": "Try again",
        "noSchool": "Curriculum shows here once you’re part of a school.",
        "noFrameworks": "No curriculum is available for your school’s state yet.",
        "cancel": "Cancel",
        "done": "Done"
      },
      "submitting": "Generating your lesson…",
      "generate": "Generate lesson",
      "errors": {
        "briefRequired": "Please describe what this lesson should cover.",
        "briefTooShort": "Add a little more detail, at least {min} characters."
      }
    },
    "comingSoon": "Coming soon",
    "everythingThatAnywayHasToOfferForStudents": "Everything that Anyway has to offer for your students.",
    "exploreStudentOutcomes": "Student activity",
    "outcomesInsights": {
      "hoursWithCoach": "student time with coach",
      "topicsCompletedByStudents": "topics completed by students",
      "topStudentForLearningModules": "to explore the most learning content",
      "hoursSuffix": "hours",
      "totalStudents": "Total students {count}"
    },
    "termsPage": {
      "title": "Teacher Terms of Use",
      "unpublishedTitle": "Terms document unavailable",
      "unpublishedDescription": "The terms document for this site has not been published yet. Please check back later.",
      "loadErrorTitle": "We couldn’t load the terms document",
      "loadErrorDescription": "Something went wrong loading the terms document. Please refresh the page or try again later."
    },
    "schoolTermsStep": {
      "title": "Join {schoolName}",
      "description": "To join {schoolName} on Anyway Schools, please review and accept the schools terms of use and privacy policy for this school.",
      "termsLink": "Terms of Use",
      "privacyLink": "Privacy Policy",
      "accept": "Agree and join",
      "accepting": "Saving your acceptance…"
    },
    "termsGate": {
      "title": "Before you continue",
      "description": "To keep using Anyway Schools, please review and accept the schools terms of use and privacy policy.",
      "termsLink": "Terms of Use",
      "privacyLink": "Privacy Policy",
      "accept": "Agree and continue",
      "accepting": "Saving your acceptance…",
      "error": "Something went wrong accepting the terms. Please try again.",
      "updatedTitle": "We’ve updated our terms",
      "updatedDescription": "We’ve made changes to the schools terms of use and privacy policy. Please review and accept the updated terms to keep using Anyway Schools.",
      "unpublishedNotice": "The terms document is not available yet, so it cannot be accepted right now. Please check back later.",
      "loadErrorNotice": "We couldn’t load the terms document, so it cannot be accepted right now. Please refresh the page to try again.",
      "school": {
        "title": "Before you continue",
        "description": "To use Anyway with {school}, please review and accept the schools terms of use and privacy policy."
      },
      "educator": {
        "title": "Review the Teacher Terms",
        "description": "To keep using Anyway Schools, please review and accept the Teacher Terms of Use and Privacy Policy. Accepting covers your own use of Anyway Schools as a teacher. It does not accept anything on behalf of your students, and it is not a purchase of a school subscription.",
        "termsLink": "Teacher Terms of Use",
        "updatedTitle": "We’ve updated the Teacher Terms",
        "updatedDescription": "We’ve made changes to the Teacher Terms of Use. Please review and accept the updated Teacher Terms to keep using Anyway Schools."
      }
    },
    "nav": {
      "lessons": "My Lessons",
      "myAccount": "My Account",
      "logout": "Logout",
      "openMenu": "Open navigation menu",
      "closeMenu": "Close navigation menu",
      "sidebarLabel": "Navigation",
      "primaryNavLabel": "Primary navigation",
      "logoText": "{productName}",
      "promoDismiss": "Dismiss",
      "students": "Student Management"
    },
    "lessons": {
      "published_at": "Published at {date}",
      "hubTitle": "Lesson Hub",
      "greeting": "Hello, {name}.",
      "welcomeBackGreeting": "Welcome back, {name}.",
      "exploreLessonsDescription": "Ready-to-go lessons curated for your classroom.",
      "learningModules": "Learning modules",
      "loadingLessons": "Loading lessons...",
      "previewButton": "Preview",
      "previewError": "Could not load preview. Please try again.",
      "previewSrDescription": "Preview of the lesson activities as a student would see them.",
      "noLessonsAvailable": "No lessons available.",
      "shareWithStudents": "Share with students",
      "templateNotFound": "Template not found.",
      "lessonsHubMyLessonsHeading": "Your active lessons",
      "lessonsHubMyLessonsDescription": "Lessons you’ve assigned to your students.",
      "tabs": {
        "active": "Active ({count})",
        "drafts": "Drafts ({count})",
        "archived": "Archived ({count})"
      },
      "drafts": {
        "discardError": "Could not discard this draft. Please try again.",
        "actions": {
          "discard": "Discard",
          "saveDraft": "Save draft"
        },
        "discardConfirm": {
          "title": "Discard this draft?",
          "message": "This draft lesson will be removed from your drafts. You can’t undo this.",
          "confirm": "Discard draft",
          "cancel": "Keep editing"
        }
      },
      "lessonsHubBrowseHeading": "Browse lessons",
      "builderCard": {
        "tag": "New",
        "heading": "Build a lesson",
        "description": "Tell us the topic and who it's for. We'll pull the best matches from the catalog below into a shareable lesson in about a minute.",
        "cta": "Start building"
      },
      "lessonsHubBrowseEmpty": "No lessons available right now.",
      "lessonsHubMyLessonsLoadError": "Could not load your lessons. Please refresh and try again.",
      "lessonsHubBrowseLoadError": "Could not load the lesson catalog. Please refresh and try again.",
      "overview": {
        "backToHub": "Back to Lesson Hub",
        "tabs": {
          "overview": "Overview",
          "insights": "Insights",
          "feedback": "Impact Report"
        },
        "status": {
          "template": "Template",
          "draft": "Draft",
          "active": "Active",
          "archived": "Archived"
        },
        "access": {
          "edit": "Edit",
          "editAria": "Edit who can access this lesson",
          "school": "Access to anyone at {school}",
          "groups": "Access to selected groups at {school}",
          "anyone": "Access to anyone with the code"
        },
        "actions": {
          "edit": "Edit",
          "shareLesson": "Share lesson",
          "accessCodeAndQr": "Access code & QR",
          "reactivate": "Share again",
          "archive": "Archive",
          "noSchoolTooltip": "You need an active school to share this lesson."
        },
        "archiveConfirm": {
          "title": "Archive this lesson?",
          "message": "Students won’t be able to open it anymore. Your insights and impact report stay, and you can re-activate it any time.",
          "confirm": "Archive lesson",
          "cancel": "Cancel"
        },
        "archiveSuccess": "Lesson archived.",
        "archiveError": "There was an error archiving this lesson. Please try again.",
        "curriculum": {
          "title": "Curriculum",
          "entrySource": "From {item}",
          "sections": {
            "outcomes": "What this lesson teaches",
            "stage": "Career development stage",
            "careers": "Careers it reveals",
            "roles": "Roles it introduces",
            "skills": "Skills it builds",
            "interests": "Interest areas it draws on"
          },
          "details": "Details",
          "previous": "Previous framework",
          "next": "Next framework",
          "position": "{current} of {total}",
          "summary": {
            "tags": "{count} standard | {count} standards",
            "outcomes": "{count} outcome | {count} outcomes",
            "stage": "{count} stage | {count} stages",
            "careers": "{count} career | {count} careers",
            "roles": "{count} role | {count} roles",
            "skills": "{count} skill | {count} skills",
            "interests": "{count} interest area | {count} interest areas"
          }
        }
      },
      "lessonsHubBrowseSearch": {
        "placeholder": "Search lessons",
        "label": "Search lessons",
        "clear": "Clear search",
        "noResultsWithBuilder": "No lessons match your search. Try Build a lesson to create your own in about a minute."
      },
      "topicsCovered": "Topics covered",
      "activateError": "There was an error activating the lesson. Please try again.",
      "activateSuccess": "Lesson published.",
      "updateAudienceError": "There was an error updating who can access this lesson. Please try again.",
      "updateAudienceSuccess": "Lesson access updated.",
      "cloneTemplateError": "We couldn’t open the lesson template for editing. Please try again.",
      "durationMinutes": "{count} minute | {count} minutes",
      "lessonLoadError": "There was an error loading the lesson. Please try again.",
      "shareLoadError": "There was an error loading the lesson share information. Please try again.",
      "shareUnavailable": "Sharing isn’t available for this lesson right now.",
      "viewInstructionsTitle": "Start by reviewing the lesson content.",
      "viewInstructionsBody": "Explore the topics, activities, and time required for this lesson. Don’t worry if you’d like to make changes — in the next step you’ll be able to choose which topics to include so the lesson fits your students’ needs.",
      "updateAccess": {
        "access": {
          "title": "Update who can access",
          "audienceScopeNotice": "Changing the audience also changes this lesson’s insights. They only ever report on the audience set here."
        },
        "targeting": {
          "title": "Update who can access"
        },
        "saveButton": "Save changes"
      },
      "edit": {
        "entryButton": "Edit lesson",
        "pageTitle": "Edit lesson",
        "nameLabel": "Lesson name",
        "nameRequired": "Lesson name is required.",
        "descriptionLabel": "Description",
        "coverLabel": "Banner image",
        "coverHint": "Optional — shown on your lesson card. We'll use a color if you don't add one. JPEG, PNG or WebP.",
        "coverCurrentAlt": "Current banner image",
        "itemsHeading": "Topics in this lesson",
        "itemsRequired": "Keep at least one lesson in this playlist.",
        "studentsWarning": "Changes will apply next time students join this lesson.",
        "removeLessonAria": "Remove {name}",
        "removeConfirmTitle": "Remove this topic?",
        "removeConfirmMessage": "\"{name}\" will be removed from this lesson. You can add it back later.",
        "removeConfirmButton": "Remove topic",
        "addLessonsButton": "Add topics",
        "curriculumUnmet": {
          "title": "Your lesson is ready, but we couldn’t match your curriculum yet",
          "description": "None of these topics are tagged against the curriculum you asked for, so we pulled in related topics for you to review. Add topics starts filtered to your curriculum."
        },
        "saveButton": "Save",
        "goBackButton": "Cancel",
        "doneButton": "Done",
        "topicsAddedCount": "{count} topic added | {count} topics added",
        "saveSuccess": "Lesson updated.",
        "saveError": "We couldn’t save your changes. Please try again.",
        "picker": {
          "title": "Add topics",
          "subtitle": "Pick from your published catalog. Added topics appear at the bottom of your lesson.",
          "searchLabel": "Search topics",
          "searchPlaceholder": "Search by topic name",
          "clearSearch": "Clear search",
          "statusBrowsing": "{count} lessons available",
          "statusFiltered": "{count} results for \"{search}\"",
          "noResultsTitle": "No matches",
          "noResultsBody": "Nothing matches \"{search}\". Try a shorter search.",
          "durationMinutes": "{count} min",
          "addButton": "Add",
          "addButtonAria": "Add {name} to this lesson",
          "addedButton": "Added",
          "removeButton": "Remove",
          "removeButtonAria": "Remove {name} from this lesson",
          "loadMore": "Load more",
          "loadError": "Could not load topics. Please refresh and try again.",
          "undoToast": "Removed \"{name}\"",
          "undoButton": "Undo",
          "curriculumFilterLabel": "Curriculum",
          "curriculumFilterAdd": "Filter by curriculum",
          "curriculumFilterAddAnother": "Add another",
          "curriculumFilterRemove": "Remove {node} filter",
          "curriculumFilterAlreadyAdded": "Already added",
          "statusFilteredByCurriculum": "{count} topics matching the chosen curriculum",
          "statusFilteredBoth": "{count} results for “{search}” in the chosen curriculum",
          "noResultsCurriculumTitle": "No topics in this curriculum",
          "noResultsCurriculumBody": "Nothing is tagged against the chosen curriculum yet. Try a broader level or clear the filter."
        }
      },
      "onboardingHero": {
        "lede": "Find ready-to-use lessons and resources to support your students wherever they are in their journey. From future pathways to life skills and classroom-ready learning, explore interactive content designed to support teachers and make learning more relevant for every student.",
        "steps": {
          "pickLesson": {
            "title": "Pick a lesson",
            "titleWithExpo": "Pick an Expo or a lesson",
            "body": "Preview the topics and student tasks before you share."
          },
          "shareQr": {
            "title": "Share the QR",
            "body": "Project it. Students scan and join instantly."
          },
          "trackEngagement": {
            "title": "Watch it land",
            "body": "Track who joined and how far they got."
          }
        }
      },
      "overviewHero": {
        "lede": "Keep track of lessons currently in progress, monitor what your students are learning, and browse our growing library of interactive lessons and resources ready to assign."
      },
      "share": {
        "stepCounter": "Step {current} of {total}",
        "scanCode": "Scan code",
        "scanQrCodeToAccess": "Scan the QR code to access this lesson",
        "qrCodeAlt": "QR code for {title}",
        "directUrl": "Direct URL",
        "enterTheCode": "Enter the code",
        "lessonPreview": "Lesson preview",
        "lessonLaunch": "Lesson launch",
        "sharingInstructions": "Share this lesson with your students using the access code or QR code below. Once students begin the lesson, you can track participation and monitor progress through Lesson Insights.",
        "backToOverview": "Back to overview",
        "coverImageAlt": "Cover image for {title}",
        "access": {
          "title": "Share this lesson",
          "question": "Who can open this lesson?",
          "questionHint": "Choose who can open this lesson, including what happens if the link is shared with someone else.",
          "options": {
            "groups": {
              "title": "Selected groups",
              "description": "Only the people you assign can open it.",
              "auth": "Students sign in",
              "visibility": "You will see each student by name"
            },
            "school": {
              "title": "Any student at {school}",
              "description": "Students must already belong to your school.",
              "auth": "Students sign in",
              "visibility": "You will see each student by name"
            },
            "anyone": {
              "title": "Anyone with the code",
              "description": "Anyone you give the code to can open it.",
              "auth": "No sign in required",
              "visibility": "You will see counts only, never names"
            }
          },
          "outcome": {
            "heading": "What this means",
            "groups": "Only students in your chosen groups can open it, and they'll need to sign in. Anyone else who gets the link is locked out.\nIt's assigned work, students see it as a to-do.",
            "anyone": "Anyone with the code can open it, with no account or sign-in required.\nIt won’t be assigned as work, and nobody is named in your results, not even students who are signed in.",
            "school": "Only {school} students can open it, and they'll need to sign in. You will see everyone's progress by name.\nIt won't be assigned as work, students are free to explore it.",
            "empty": "Pick an option above to see exactly what you will get."
          },
          "targeting": {
            "title": "Share this lesson",
            "question": "Who is this lesson for?",
            "questionHint": "Pick the groups this lesson is set for."
          },
          "impactReport": {
            "title": "Impact Report",
            "question": "Enable student feedback?",
            "questionHint": "Students answer a few questions when they finish a lesson. Results land on the impact report.",
            "label": "Turn on Impact Report"
          },
          "review": {
            "title": "Ready to share?",
            "questionHint": "Review and share",
            "lesson": "Lesson",
            "lessonLabel": "{name} · {count} topic · {duration} | {name} · {count} topics · {duration}",
            "access": "Sharing with",
            "schoolAccessLabel": "Anyone at {school}",
            "groupsAccessLabel": "Selected groups at {school}",
            "impactReport": "Student feedback",
            "impactReportOnLabel": "Impact Report enabled",
            "impactReportOffLabel": "Impact Report disabled",
            "anyoneAccessLabel": "Anyone with the code"
          }
        },
        "continueButton": "Continue to sharing"
      },
      "statistics": {
        "anonymous": {
          "label": "{total} Anonymous user | {total} Anonymous users",
          "summary": "{started} started · {inProgress} in progress · {completed} completed"
        },
        "completed": "Completed",
        "inProgress": "In progress",
        "started": "Started",
        "studentsCompleted": "{count} completed",
        "studentsInProgress": "{count} in progress",
        "studentsStarted": "{count} started",
        "completedOutOfTotalStudents": "{completed} of {total} students completed",
        "ofStudents": "of {count} students",
        "lessonItemBreakdown": "Activity breakdown",
        "students": "Students",
        "lastRefreshed": "Last refreshed {time}",
        "studentProgress": "Student Progress",
        "studentName": "Student Name",
        "lessonStatus": "Lesson Status",
        "lessonAchieved": "Achieved",
        "lessonAttempted": "Attempted",
        "lessonNotStarted": "Not started",
        "rosterScope": "Every student this lesson was set for",
        "topicCount": "{count} topic | {count} topics",
        "notCompleted": "Not completed",
        "chartAriaLabel": "{completedBy} of {total} students completed {title}",
        "anonymousStudent": "Anonymous student"
      },
      "generateError": "We couldn't generate that playlist. Please try again.",
      "create": {
        "brief": {
          "label": "Lesson brief",
          "placeholder": "e.g. An introduction to careers in renewable energy for 10th grade students."
        },
        "audience": {
          "label": "Target audience",
          "placeholder": "e.g. 10th grade STEM students"
        },
        "submitting": "Generating your lesson…",
        "errors": {
          "briefRequired": "Please describe what this playlist should cover.",
          "briefTooShort": "Add a little more detail, at least {min} characters."
        }
      },
      "generationNotes": {
        "title": "Why these items?",
        "subtitle": "Built from your brief. Edit anything before you share.",
        "aiGenerated": "AI generated",
        "warningsTitle": "Heads up",
        "warnings": {
          "droppedAssets": "Some suggested items were not in the available pool and were left out: {assets}.",
          "nameTruncated": "Collection name shortened to {max} characters.",
          "descriptionTruncated": "Description shortened to {max} characters.",
          "assetsUnavailable": "{count} suggested item(s) were no longer available and were left out of this playlist."
        }
      },
      "feedback": {
        "optIn": {
          "title": "Impact Report",
          "saveError": "We could not save that. Please try again.",
          "previewLink": "Preview questions"
        },
        "responses": {
          "count": "{count} response | {count} responses",
          "loading": "Loading feedback…",
          "loadError": "We could not load the feedback. Please try again.",
          "emptyTitle": "No feedback yet",
          "emptyBody": "Answers appear here as students finish the lesson.",
          "emptyBodyOptedOut": "Turn the impact report on and students will be asked when they finish.",
          "collectionOff": "Impact report is off, so this list has stopped growing. Everything already collected is kept.",
          "unnamedStudent": "Anonymous student",
          "noAnswer": "Not answered",
          "agreed": "agreed with {count} of {total}",
          "showAnswers": "Show all answers",
          "hideAnswers": "Hide answers"
        },
        "report": {
          "statusCollecting": "Collecting feedback",
          "statusStopped": "Collection stopped",
          "responseRate": "{percent}% of the {completed} student who finished | {percent}% of the {completed} students who finished",
          "noCompleters": "No one has finished the lesson yet",
          "headlineLead": "{overall}% rated their experience Excellent or Good.",
          "headlineSpread": "{strongest} scored highest. {softest} scored lowest.",
          "keyInsights": "Key insights",
          "keyInsightsHint": "Select a figure to see how the class split",
          "theRest": "And the rest",
          "theRestHint": "Share who agreed or strongly agreed",
          "seeSplit": "See class split",
          "hideSplit": "Hide class split",
          "noAnswersYet": "No answers yet",
          "outOf": "/ {max}",
          "showBreakdown": "Show the full breakdown",
          "hideBreakdown": "Hide the full breakdown",
          "breakdownCaption": "Student counts by response. Base: {count} response. | Student counts by response. Base: {count} responses.",
          "breakdownStatement": "Statement",
          "breakdownAgreed": "Agreed",
          "showResponses": "Show individual responses",
          "hideResponses": "Hide individual responses",
          "loading": "Loading the report…",
          "loadError": "We could not load the report. Please try again.",
          "fewResponses": "Only {count} response so far. Read it as an early signal, not the class. | Only {count} responses so far. Read these as early signals, not the class.",
          "empty": {
            "title": "No responses yet",
            "body": "Students answer a short set of questions as they finish this lesson. Their results land here."
          },
          "preview": {
            "title": "The student survey",
            "caption": "This is a preview. Answers are not saved.",
            "loadError": "We could not load the survey. Please try again."
          },
          "collectionOff": "Impact report is off, so these figures have stopped changing. Everything already collected is kept.",
          "metrics": {
            "subjectEngagement": {
              "label": "Class engagement",
              "caption": "More motivated to engage with this class"
            },
            "connectToCareers": {
              "label": "Job connections",
              "caption": "Better understood how this class connects to real jobs"
            },
            "discoveredPathway": {
              "label": "Career discovery",
              "caption": "Discovered or considered a new career pathway"
            },
            "useAgain": {
              "label": "Repeat sentiment",
              "caption": "Would use Anyway again",
              "captionWithRecommend": "Would use Anyway again. {mean} / {max} would recommend it to a friend"
            },
            "overallExperience": {
              "label": "Overall experience",
              "caption": "Rated their experience Excellent or Good"
            },
            "futureMotivation": {
              "label": "Future motivation",
              "caption": "More motivated to think about their future"
            },
            "futureConfidence": {
              "label": "Future confidence",
              "caption": "More confident planning their next steps"
            },
            "coachValue": {
              "label": "AI Coach value",
              "caption": "The AI Coach helped them think more deeply"
            }
          }
        }
      },
      "expos": {
        "explainer": {
          "title": "What’s an Expo?",
          "body": "Every 2 weeks we launch a new content experience to help connect your students’ career education to real world calendar events. Each Expo features a ready-to-run lesson tied to a real-world event, giving students an immersive, hands-on taste of what different careers actually feel like before committing to them.",
          "note": "They’re only live for a limited time, so run one with your class while it’s open."
        },
        "featured": {
          "heading": "Featured lessons",
          "description": "New content experience to help connect your students’ career education to real world calendar events."
        },
        "card": {
          "live": "Live now",
          "cta": "View expo",
          "minutes": "{count} minute | {count} minutes",
          "activities": "{count} activity | {count} activities",
          "until": "on until {date}"
        }
      }
    },
    "jobTitle": {
      "option": {
        "careerCounselor": "Career Counselor",
        "cteTeacher": "CTE Teacher",
        "teacher": "Teacher",
        "administrator": "Administrator"
      }
    },
    "auth": {
      "teacherRegisterHeader": "Tell us a bit about you",
      "teacherContinue": "Continue",
      "registrationComplete": {
        "teacher": {
          "header": "You're all set",
          "helpText": "Your teacher account has been successfully created."
        },
        "actionButton": "Get Started"
      },
      "passwordSetup": {
        "description": "Log in without waiting for an emailed code. You can always add one later from My Account.",
        "tokenExpired": "That took a little too long. You can add a password any time from My Account."
      },
      "signInExpired": "That sign-in took too long. Enter your email to start again.",
      "chooseAccount": {
        "title": "Choose an account",
        "description": "Your email is linked to more than one school account. Pick the one to sign in to.",
        "personal": "Personal",
        "roles": {
          "teacher": "Teacher",
          "student": "Student",
          "admin": "Admin"
        },
        "submit": "Continue",
        "retry": "Try again"
      }
    },
    "accountSwitcher": {
      "trigger": "Switch account, signed in at {schoolName}",
      "switching": "Switching to {schoolName}",
      "errors": {
        "rateLimited": "Too many attempts. Please wait a few minutes and try again.",
        "failed": "We could not switch accounts. Please try again."
      }
    },
    "feedback": {
      "title": "Feedback",
      "subtitle": "Tell us what you think",
      "lede": "Share your feedback, ideas, or report a problem.",
      "categoryLabel": "Feedback category",
      "category": {
        "featureSuggestion": "Feature suggestion",
        "bugReport": "Bug report",
        "lessonRequest": "Lesson request",
        "generalFeedback": "General feedback"
      },
      "commentLabel": "Tell us more",
      "commentPlaceholder": "Eg. I did not feel its…",
      "commentTooShort": "Please enter at least {num} characters.",
      "commentTooLong": "Please keep your feedback to {num} characters or fewer.",
      "followUpLabel": "Can we follow up with you?",
      "followUpDescription": "We may reach out if we need more context.",
      "submit": "Submit",
      "privacyNote": "Your feedback is private and only seen by our team.",
      "submittedToast": "Thanks for your feedback!",
      "cta": {
        "heading": "Tell us what you think",
        "button": "Share feedback"
      }
    },
    "studentManagement": {
      "title": "Student Management",
      "managedKept": {
        "single": "{name} is managed by your school’s roster and was kept.",
        "all": "{count} student is managed by your school’s roster and was kept. | {count} students are managed by your school’s roster and were kept.",
        "partial": "{removed} removed. {count} student is managed by your school’s roster and was kept. | {removed} removed. {count} students are managed by your school’s roster and were kept.",
        "noneRemovable": "The selected student is managed by {connection}, so they can’t be removed here. | All {total} selected students are managed by {connection}, so none can be removed here."
      },
      "verification": {
        "lede": "Once {schoolName} is verified, you can add students here and track every invitation from sent to accepted."
      },
      "rosterSource": {
        "banner": "Roster managed by {connection} through {provider}. Students are added and removed in {provider} by your district; changes arrive on the next sync.",
        "lede": "Students at {schoolName} come from your school’s roster. Monitor their progress here.",
        "managedBy": "Managed by {connection}. Ask your district to remove them in {provider}.",
        "managedHint": "Managed by {connection}. Its roster keeps their name and grade up to date.",
        "editHint": "Managed by {connection}. Its roster keeps this student’s name and grade up to date.",
        "noGrade": "No grade from {provider}",
        "manual": "Added manually",
        "manualHint": "Added at your school, not by {connection}’s roster. You can edit and remove them.",
        "paused": "Paused",
        "pausedHint": "{connection} is paused, so no syncs run for now. Students stay as they are, and changes arrive once it resumes.",
        "filter": "Show",
        "connection": "From {provider}",
        "emptyLabel": "No students yet",
        "emptySubtitle": "Students appear here after the next sync from {provider}.",
        "cards": {
          "neverSignedIn": {
            "label": "Not signed in yet",
            "sublabel": "Still to get started"
          },
          "manual": {
            "label": "Added manually",
            "sublabel": "You can edit and remove these"
          },
          "lastUpdated": {
            "label": "Roster last updated",
            "sublabel": "Synced from {provider}",
            "never": "Waiting for first sync"
          }
        }
      },
      "greeting": "Hi {name}, let's build your roster.",
      "welcomeBackGreeting": "Welcome back, {name}",
      "onboardingHero": {
        "lede": "{schoolName} has no students yet. Add them and we'll track every invitation from sent to accepted.",
        "steps": {
          "invite": {
            "title": "You invite",
            "body": "Add students individually or from a class list."
          },
          "join": {
            "title": "They join",
            "body": "Students accept the invite and create an account."
          },
          "manage": {
            "title": "You track",
            "body": "Every invitation shows here, from sent to accepted."
          }
        }
      },
      "overviewHero": {
        "lede": "Invite and manage students at {schoolName}. Track invitation status and monitor student progress."
      },
      "detail": {
        "back": "Back to student management",
        "editAction": "Edit student",
        "gradeRequired": "Choose a grade",
        "tabs": {
          "overview": "Overview",
          "lessons": "Lessons",
          "portfolio": "Portfolio"
        },
        "portfolio": {
          "loadFailed": "Couldn't load this student's portfolio. Refresh to try again."
        },
        "plan": {
          "title": "{name}’s Career Development Plan",
          "printedOn": "Printed {date}"
        },
        "progress": {
          "title": "Lessons",
          "legend": {
            "notStarted": "Not started",
            "inProgress": "In progress",
            "completed": "Completed"
          },
          "empty": "No lessons yet",
          "emptyDescription": "Lessons assigned to {name} and lessons open to your whole school will show up here.",
          "assignedBy": "Assigned by {name}",
          "assignedByYou": "Assigned by you",
          "openToWholeSchool": "Open to whole school",
          "itemsLabel": "{completed} of {total} complete",
          "status": {
            "COMPLETED": "Finished {date}",
            "IN_PROGRESS": "Started {date}",
            "NOT_OPENED": "Not opened"
          },
          "seeAll": "See all {count} lessons",
          "today": "today",
          "dateEmpty": "–",
          "itemStatus": {
            "COMPLETED": "Completed",
            "IN_PROGRESS": "Started",
            "NOT_STARTED": "Not started"
          },
          "itemsEmpty": "This lesson has no parts yet",
          "assignedTime": "assigned {age}",
          "publishedTime": "published {age}",
          "loadFailed": "Couldn't load this student's lessons. Refresh to try again."
        },
        "header": {
          "ringLabel": "Completed",
          "joined": "joined {date}",
          "summaryNone": "No lessons set yet.",
          "summaryLead": "{completed} of {total} lessons finished.",
          "breakdown": "{completed} finished · {inProgress} underway · {notOpened} not opened",
          "ringTipLessons": "{completed} of {total} lessons finished ({percent}%)",
          "ringTipStates": "{inProgress} underway · {notOpened} not opened",
          "ringTipParts": "{completed} of {total} lesson parts complete",
          "summaryRest": "No lessons underway. | {inProgress} underway. | {inProgress} underway.",
          "summaryNotOpened": "{count} not opened since it was assigned. | {count} not opened since they were assigned."
        },
        "career": {
          "title": "Career discovery",
          "loadFailed": "We couldn't load this student's career discovery. Refresh to try again.",
          "empty": "No career discovery activity yet",
          "emptyDescription": "{name} hasn't started exploring careers on Anyway yet.",
          "clarity": {
            "title": "Career clarity",
            "ringLabel": "Career clarity score: {percentage} percent. {risk}",
            "lowBody": "{name} has a clear picture of where they're heading and what to explore next.",
            "mediumBody": "{name} is building a picture of their future. Momentum matters most right now.",
            "highBody": "{name} is early in their career exploration and may benefit from a nudge."
          },
          "risk": {
            "low": "Low risk",
            "medium": "Medium risk",
            "high": "High risk"
          },
          "stats": {
            "careers": "Careers saved",
            "institutions": "Institutions saved",
            "badges": "Badges earned"
          },
          "areas": {
            "title": "Most explored areas",
            "level": "Level {level}",
            "progressLabel": "{label}: level {level}, {progress} percent progress"
          },
          "careers": {
            "title": "Saved careers",
            "more": "+{count} more"
          },
          "institutions": {
            "title": "Saved institutions",
            "more": "+{count} more"
          },
          "discovery": {
            "title": "Self-discovery results",
            "persona": "Persona",
            "interest": "Top interest",
            "workValue": "Top work value",
            "strength": "Strength"
          },
          "badges": {
            "title": "Badges"
          }
        }
      },
      "portfolio": {
        "title": "Portfolio",
        "readOnly": "Read-only view",
        "aboutMe": "About me",
        "education": "Education",
        "workExperience": "Work experience",
        "achievements": "Achievements",
        "evidence": "Evidence",
        "present": "present",
        "dateRange": "{start} – {end}",
        "uploaded": "Uploaded {date}",
        "empty": {
          "aboutMe": "No bio added yet",
          "education": "No education added yet",
          "workExperience": "No work experience added yet",
          "achievements": "No achievements added yet",
          "evidence": "No evidence added yet"
        }
      },
      "actions": {
        "addStudents": "Add students",
        "uploadClassList": "Upload a class list",
        "uploadClassListHint": "Invite many students at once from a spreadsheet",
        "addOneStudent": "Add one student",
        "addOneStudentHint": "For a single student or a late enrollment",
        "manageGroups": "Groups"
      },
      "groups": {
        "title": "Groups",
        "subtitle": "Organize students into the groups you teach, like classes, small groups or cohorts. Groups are shared with all educators at {schoolName}.",
        "empty": "No groups yet. Create your first group to organize your roster.",
        "limitReached": "Your school has reached the maximum of {limit} groups.",
        "createdBy": "Created by {name}",
        "fields": {
          "name": "Group name",
          "namePlaceholder": "e.g. Period 3 Biology, Reading Group A",
          "colour": "Group color"
        },
        "actions": {
          "new": "New group",
          "save": "Save",
          "cancel": "Cancel",
          "rename": "Rename {name}",
          "delete": "Delete {name}",
          "rowAction": "Groups",
          "addToGroup": "Add to group",
          "addStudents": "Add students",
          "createAndAdd": "Create and add students",
          "skipForNow": "Skip for now",
          "more": "More actions for {name}"
        },
        "errors": {
          "nameRequired": "Enter a group name"
        },
        "deleteConfirm": {
          "title": "Delete {name}?",
          "message": "Educators at your school will no longer be able to use this group. This can’t be undone.",
          "confirm": "Delete group",
          "cancel": "Keep group"
        },
        "colours": {
          "red": "Red",
          "orange": "Orange",
          "amber": "Amber",
          "green": "Green",
          "teal": "Teal",
          "blue": "Blue",
          "violet": "Violet",
          "pink": "Pink"
        },
        "picker": {
          "manageTitle": "Groups for {name}",
          "manageSubtitle": "Pick the groups this student belongs to.",
          "bulkTitle": "Add {count} student to a group | Add {count} students to a group",
          "bulkInviteSkipNotice": "{count} pending invite in your selection can't be added and will be skipped. | {count} pending invites in your selection can't be added and will be skipped.",
          "capHint": "You can pick up to {limit} groups. Uncheck one to add another.",
          "empty": "No groups yet.",
          "save": "Save",
          "addStudents": "Add students",
          "cancel": "Cancel",
          "createGroup": "Create group",
          "createNamed": "Create \"{name}\"",
          "createAndSelect": "Create and select",
          "created": "{name} created and selected",
          "createdAtCap": "{name} created. You can pick up to {limit} groups. Uncheck one to select it."
        },
        "search": {
          "placeholder": "Search groups",
          "label": "Search groups",
          "noResults": "No groups match {query}"
        },
        "chips": {
          "overflow": "+{count}",
          "overflowAria": "{count} more group | {count} more groups"
        },
        "results": {
          "added": "Added {added} student to {group}. | Added {added} students to {group}.",
          "addedSkippedMembers": "Added {added} student to {group}. Skipped {members} already in the group. | Added {added} students to {group}. Skipped {members} already in the group.",
          "addedSkippedCap": "Added {added} student to {group}. Skipped {cap} at the {limit}-group limit. | Added {added} students to {group}. Skipped {cap} at the {limit}-group limit.",
          "addedSkippedBoth": "Added {added} student to {group}. Skipped {members} already in the group and {cap} at the {limit}-group limit. | Added {added} students to {group}. Skipped {members} already in the group and {cap} at the {limit}-group limit.",
          "noneAddedMembers": "No students added to {group}. Skipped {members} already in the group.",
          "noneAddedCap": "No students added to {group}. Skipped {cap} at the {limit}-group limit.",
          "noneAddedBoth": "No students added to {group}. Skipped {members} already in the group and {cap} at the {limit}-group limit.",
          "noneEligible": "None of the selected students can be added to a group. Only active students are eligible."
        },
        "invite": {
          "label": "Groups (optional)",
          "none": "Not in any groups yet",
          "assign": "Assign to groups",
          "edit": "Edit groups",
          "viewTitle": "Add to groups",
          "back": "Back",
          "done": "Done",
          "successNote": "They'll be added to {groups} when they accept their invite."
        },
        "bulk": {
          "title": "Add to groups (optional)",
          "intro": "All {count} students you're inviting will be added to the selected groups. You can skip this step.",
          "next": "Next",
          "summaryLabel": "Adding invited students to",
          "summaryNone": "No groups selected"
        },
        "memberCount": "{count} student | {count} students",
        "members": {
          "subtitle": "{count} student in this group. Check to add, uncheck to remove. | {count} students in this group. Check to add, uncheck to remove.",
          "back": "All groups",
          "searchPlaceholder": "Search students by name or email",
          "searchLabel": "Search students",
          "filterLabel": "Filter students",
          "filter": {
            "all": "All students",
            "inGroup": "In group ({count})",
            "notInGroup": "Not in group"
          },
          "pendingLocked": "Only active students can be added here for now",
          "pendingMember": "Added at invite time, joins when they accept",
          "capLocked": "Already in {limit} groups",
          "empty": "No students match that search.",
          "truncated": "Showing the first {count} students. This school has more than this view can load, so some students are not listed.",
          "nothingToSave": "Nothing to save yet",
          "summaryAdd": "{count} student to add | {count} students to add",
          "summaryRemove": "{count} to remove",
          "save": "Save changes",
          "saved": "Changes saved. {group} now has {count} student. | Changes saved. {group} now has {count} students.",
          "createdSubtitle": "Group created. Pick who belongs in it, or skip and do it later."
        },
        "contextBar": {
          "viewing": "Viewing {name}",
          "addStudents": "Add students",
          "manageGroup": "Manage group"
        }
      },
      "cards": {
        "total": {
          "label": "Total students",
          "sublabel": "All students"
        },
        "invited": {
          "label": "Awaiting activation",
          "sublabel": "Invited students"
        },
        "expired": {
          "label": "Invitations expired",
          "sublabel": "Needs attention"
        },
        "deliveryFailed": {
          "label": "Delivery failures",
          "sublabel": "Needs attention"
        },
        "unavailable": "Unavailable",
        "empty": "–"
      },
      "table": {
        "title": "Students",
        "searchPlaceholder": "Search by name or email",
        "columns": {
          "student": "Student",
          "year": "Grade",
          "status": "Status",
          "lastActive": "Last active",
          "lastActiveEmpty": "–",
          "groups": "Groups",
          "groupsEmpty": "–",
          "actions": "Actions"
        },
        "actions": {
          "viewDetails": "View details",
          "resendSuccess": "Invite resent to {email}",
          "remove": "Remove",
          "removeSuccess": "{name} removed from your school",
          "removeConfirm": {
            "title": "Remove {name}?",
            "messageStudent": "They’ll lose access to your school but keep their personal account. You can re-invite them later.",
            "messageStudentAtManagedSchool": "They’ll lose access to your school but keep their personal account. You can’t re-invite them while {connection} manages your roster.",
            "messageInvite": "Their invitation will be canceled. You can re-invite them later.",
            "confirmText": "Remove"
          }
        },
        "bulkActions": {
          "resend": "Resend invites",
          "remove": "Remove students",
          "removeConfirm": {
            "title": "Remove selected students?",
            "message": "{total} selected students and pending invites will be removed from your school. Active students keep their personal accounts.",
            "messageNoneManaged": "{total} selected student will be removed from your school. They keep their personal account. | {total} selected students will be removed from your school. They keep their personal accounts.",
            "messageSomeManaged": "{removable} of {total} selected students will be removed. The other one is managed by {connection} and stays. | {removable} of {total} selected students will be removed. The other {managed} are managed by {connection} and stay.",
            "confirmText": "Remove"
          },
          "confirm": {
            "title": "Resend invites?",
            "message": "{eligible} of {total} selected students have an invite that can be resent. Active students are skipped.",
            "confirmText": "Resend"
          },
          "results": {
            "success": "No invites resent. | {count} invite resent. | {count} invites resent.",
            "withSkipped": "No invites resent, {skipped} skipped. | {count} invite resent, {skipped} skipped. | {count} invites resent, {skipped} skipped.",
            "noneEligible": "None of the selected students have an invite to resend.",
            "removeSuccess": "No students removed. | {count} student removed. | {count} students removed.",
            "removePartialRolesFailed": "Invites removed, but removing students failed. Try again.",
            "removePartialInvitesFailed": "Students removed, but removing invites failed. Try again."
          }
        },
        "filters": {
          "status": "Status",
          "year": "Grade",
          "groups": "Groups",
          "groupsSearchPlaceholder": "Find a group",
          "groupsEmpty": "No groups yet. Create one from Manage groups."
        },
        "status": {
          "active": "Active",
          "invited": "Invited",
          "inviteExpired": "Invite expired",
          "deliveryFailed": "Delivery failed",
          "pendingIdentity": "Not signed in yet"
        },
        "statusDetail": {
          "sent": "Sent {timeAgo}",
          "expired": "Expired {timeAgo}",
          "failed": "Failed {timeAgo}",
          "pendingIdentity": "They’ll confirm with a code at their next {provider} sign-in."
        },
        "yearNotSet": "Not set",
        "loading": "Loading students...",
        "noResults": "No students found",
        "noResultsSubtitle": "No students match your current search or filters.",
        "unavailable": "Students unavailable",
        "unavailableSubtitle": "The student list could not be loaded. Refresh the page to try again."
      },
      "invite": {
        "title": "Invite a student",
        "subtitle": "They’ll get an email invitation to create an account and join {schoolName}. It’s valid for {expiryDays} days.",
        "fields": {
          "firstName": "First name",
          "lastName": "Last name",
          "email": "Email",
          "year": "Grade (optional)",
          "gradePlaceholder": "Select grade"
        },
        "errors": {
          "required": "This field is required",
          "email": "Enter a valid email address",
          "whitespace": "This field can’t be only spaces"
        },
        "submit": "Send invitation",
        "headings": {
          "active": "Student already active",
          "pending": "Invitation pending",
          "expired": "Invitation expired",
          "failed": "Delivery failed",
          "success": "Invitation sent"
        },
        "success": {
          "title": "Invitation sent to {email}"
        },
        "actions": {
          "another": "Invite another",
          "done": "Done",
          "resend": "Resend invite"
        },
        "duplicate": {
          "active": "{email} is already an active student at {schoolName}.",
          "viewStudent": "View student",
          "pending": "An invitation is already pending — last sent {lastSent}.",
          "expired": "The previous invitation expired. Check the email and resend.",
          "failed": "The previous invitation failed to deliver. Check the email and resend."
        }
      },
      "bulk": {
        "title": "Upload CSV",
        "step": {
          "upload": "Upload",
          "groups": "Groups",
          "preview": "Preview",
          "result": "Result"
        },
        "downloadTemplate": "Download CSV template",
        "upload": {
          "intro": "Add lots of students at once. Upload a CSV file and we'll check every row, then show you a preview before any invitations go out.",
          "columnsTitle": "Your file needs a column for each of these:",
          "optionalSuffix": "(optional)",
          "maxRows": "Up to {count} students per file.",
          "dropzoneTitle": "Drag and drop your CSV file here"
        },
        "processing": "Checking your file...",
        "preview": {
          "readySummary": "{ready} of {total} ready to invite",
          "distributionAria": "Breakdown of uploaded rows"
        },
        "category": {
          "ready": "Ready to invite",
          "needsAttention": "Needs attention",
          "active": "Already active",
          "pending": "Invite already pending",
          "duplicate": "Duplicate within file"
        },
        "reasons": {
          "invalidEmail": "Invalid email address",
          "missingName": "First and last name are required",
          "unknownYear": "Grade isn't a valid option",
          "duplicateEmail": "Duplicate email within this file",
          "alreadyActive": "Already active",
          "pendingInvite": "Invite already pending",
          "failedToSend": "Failed to send - try again"
        },
        "errorReport": {
          "reasonHeader": "Reason"
        },
        "csvErrors": {
          "aboveMaxRows": "The uploaded CSV's number of rows are larger than the {limit}.",
          "missingColumns": "The uploaded CSV has a missing column: {columns}.",
          "noData": "The uploaded CSV has no data.",
          "others": "There is some issue with the CSV file. Please re-upload."
        },
        "actions": {
          "uploadCorrected": "Upload corrected file",
          "downloadErrors": "Download error report",
          "continueValid": "Continue with {count} valid rows",
          "viewStudents": "View students",
          "changeGroups": "Change groups"
        },
        "result": {
          "title": "Invitations sent",
          "invited": "{count} invited",
          "existingSkipped": "{count} already active (skipped)",
          "pendingSkipped": "{count} already pending (skipped)",
          "invalidSkipped": "{count} invalid (skipped)",
          "failed": "{count} failed to send",
          "downloadUnsuccessful": "Download unsuccessful rows",
          "invitedWithGroups": "{count} invited and added to {groups}"
        }
      }
    },
    "verification": {
      "title": "Your classroom needs to be verified",
      "body": "Students can join {schoolName} once we’ve verified it. Ask us to verify it and we’ll be in touch.",
      "action": "Request verification",
      "sent": "Request sent. We’ll be in touch once your classroom is verified."
    }
  }
}
